import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_ADD_IN_DATA } from "@/constants/resultsLimits";
import { decompressAddIns } from "@/utilities/compression";
import { isMissingPermissionError } from "@/utilities/createQueryClient";

export interface CastedAddInData<T = unknown> {
  addInId: string;
  id: string;
  details: T;
}

/**
 * Returns the addin data of the given id (up to RESULTS_LIMIT_ADD_IN_DATA).
 *
 * @param addInId - geotab addin id.
 * @returns - {@link AddinData[]}.
 */
export function useRawAddinData<T = unknown>(
  addInId: string,
  disabled?: boolean,
): UseQueryResult<CastedAddInData<T>[]> {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery<CastedAddInData<T>[]>({
    queryKey: ["addinData", addInId, activeSessionId],

    queryFn: async ({ signal }) => {
      if (!addInId) {
        return Promise.resolve([]);
      }

      const data = await geotabClient.get("AddInData", { addInId }, RESULTS_LIMIT_ADD_IN_DATA, signal);
      return decompressAddIns(data) as unknown as CastedAddInData<T>[];
    },

    // A user without the clearance gets the same refusal every 30 s; stop asking until remount.
    refetchInterval: query => (isMissingPermissionError(query.state.error) ? false : 30000),
    placeholderData: [],
    enabled: !disabled,
  });
}

/**
 * Extracts only the `details` field from the raw addin data.
 */
export function useAddinData<T = unknown>(
  addInId: string,
  disabled?: boolean,
): UseQueryResult<T[]> {
  const queryResult = useRawAddinData<T>(addInId, disabled);

  return {
    ...queryResult,
    data: queryResult.data ? queryResult.data.map(d => d.details) : [],
  } as UseQueryResult<T[]>;
}
